import React, { useMemo } from 'react'
import { cn } from '@/utilities/ui'

export default function AsyncSubmitButton({
  text = '',
  disabled = false,
  loading = false,
  completed = false,
  primary = true,
  danger = false,
  variant = '',
  autoWidth = false,
  customStyles = '',
  keepColorOnComplete = false,
  completedText = '',
  textIcon = null,
  completedIcon = null,
  loadingIcon = null,
  onSubmit,
  className = '',
  variantClasses,
  ariaLabel,
  type = 'button',
  ...buttonProps
}: {
  text?: string
  disabled?: boolean
  loading?: boolean
  completed?: boolean
  primary?: boolean
  danger?: boolean
  variant?: string
  autoWidth?: boolean
  customStyles?: string
  keepColorOnComplete?: boolean
  completedText?: string
  textIcon?: React.ElementType<React.SVGProps<SVGSVGElement>> | null
  completedIcon?: React.ElementType<React.SVGProps<SVGSVGElement>> | null
  loadingIcon?: React.ElementType<React.SVGProps<SVGSVGElement>> | null
  onSubmit?: () => void
  className?: string
  variantClasses?: {
    base?: string
    hover?: string
    active?: string
  }
  ariaLabel?: string
  type?: 'button' | 'submit' | 'reset'
} & Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'type' | 'className' | 'disabled' | 'onClick' | 'aria-label' | 'aria-busy'
>) {
  const isLoading = loading && !completed
  const isCompleted = completed

  const buttonClasses = useMemo(() => {
    const resolvedVariant = variant
      ? variant
      : danger
        ? 'danger'
        : primary
          ? 'primary'
          : 'secondary'

    const variantClassMap = {
      primary: 'bg-bright-blue text-white hover:bg-bright-blue-hover active:bg-bright-blue-active',
      secondary:
        'bg-transparent border-bright-blue text-bright-blue! hover:bg-blue-50 active:bg-blue-100',
      danger: primary
        ? 'bg-coral text-white hover:bg-coral-hover active:bg-coral-active'
        : 'bg-white border-coral text-coral hover:bg-red-50 active:bg-red-100',
      navy: 'bg-dark-blue text-white hover:bg-dark-blue active:bg-dark-blue',
    }

    return cn(
      'border w-full text-base h-[48px] rounded-full text-center flex justify-center items-center transition-all duration-200 cursor-pointer',
      !isLoading &&
        !isCompleted &&
        (variantClasses && (variantClasses.base || variantClasses.hover || variantClasses.active)
          ? cn(variantClasses.base, variantClasses.hover, variantClasses.active)
          : variantClassMap[resolvedVariant as keyof typeof variantClassMap] ||
            variantClassMap.primary),
      {
        'bg-disabled-gray! border-none! text-white! cursor-not-allowed hover:bg-disabled-gray! active:bg-disabled-gray!':
          isLoading,
        'bg-disabled-gray! border-none! cursor-not-allowed hover:bg-disabled-gray! active:bg-disabled-gray!':
          disabled && !isLoading && !isCompleted,
        'px-5': autoWidth,
        'xs:w-[240px]': !autoWidth,
        'cursor-not-allowed hover:bg-inherit! active:bg-inherit!':
          isCompleted && keepColorOnComplete,
        'bg-light-grey! border-light-grey! cursor-not-allowed hover:bg-light-grey! active:bg-light-grey!':
          isCompleted && !keepColorOnComplete,
      },
      customStyles,
      className,
    )
  }, [
    autoWidth,
    className,
    customStyles,
    danger,
    disabled,
    isCompleted,
    isLoading,
    keepColorOnComplete,
    primary,
    variant,
    variantClasses,
  ])

  const handleSubmit = () => {
    if (!isLoading && !isCompleted && !disabled) {
      onSubmit?.()
    }
  }

  return (
    <button
      {...buttonProps}
      type={type}
      className={buttonClasses}
      disabled={disabled || loading || completed}
      onClick={handleSubmit}
      aria-label={ariaLabel || 'Submit Button'}
      aria-busy={isLoading}
    >
      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center cursor-not-allowed">
          {loadingIcon ? (
            React.createElement(loadingIcon, {
              className: 'w-5 h-5 animate-spin text-white',
              'aria-hidden': true,
            })
          ) : (
            /* eslint-disable-next-line @next/next/no-img-element */
            <img
              src="/icons/buttonLoader.svg"
              width="20"
              height="20"
              alt=""
              aria-hidden={true}
              className="max-w-full animate-spin"
            />
          )}
        </div>
      )}

      {/* Completed State */}
      {isCompleted && (
        <div
          className="flex items-center justify-center cursor-not-allowed"
          data-testid="async-submit-completed"
        >
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-light-green rounded-full flex items-center justify-center success-pop">
              <svg
                className="w-4 h-4 text-white success-check"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            {completedText && (
              <>
                {completedIcon &&
                  React.createElement(completedIcon, {
                    className: 'w-4 h-4 text-dark-blue',
                    'aria-hidden': true,
                  })}
                <span className="text-dark-blue" aria-live="polite">
                  {completedText}
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Default State */}
      {!isLoading && !isCompleted && (
        <span className={cn({ 'cursor-not-allowed': disabled, 'inline-flex': !text })}>
          {textIcon ? (
            <span className="inline-flex items-center gap-2">
              {React.createElement(textIcon, {
                className: `w-4 h-4 ${primary ? 'text-white' : 'text-bright-blue'}`,
                'aria-hidden': true,
              })}
              {text && <span>{text}</span>}
            </span>
          ) : (
            text
          )}
        </span>
      )}
      {/* Success state animations scoped to this component */}
      <style jsx>{`
        @keyframes success-pop {
          0% {
            transform: scale(0.5);
            opacity: 0;
          }
          60% {
            transform: scale(1.15);
            opacity: 1;
          }
          100% {
            transform: scale(1);
          }
        }

        @keyframes check-fade {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .success-pop {
          animation: success-pop 400ms cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
        }

        .success-check {
          animation: check-fade 300ms ease-out 150ms both;
        }

        .success-check :global(path) {
          stroke-dasharray: 28;
          stroke-dashoffset: 28;
          animation: check-draw 420ms ease-out 120ms forwards;
        }

        @keyframes check-draw {
          to {
            stroke-dashoffset: 0;
          }
        }
      `}</style>
    </button>
  )
}
